import { supabase } from './supabase';

// Quick links (Gary, 15 Oct): ready messages with a link, picked from the + menu, put in the
// message box to check and send. Operator pages and Google reviews come from each operator's
// website settings in Sentinel (chat_quick_link_operators); "Our pages" is the list head office
// keeps in the Portal (chat_quick_links). Wording drafted 15 Oct, for Gary to approve.
// The link goes as a button under the words, never as a raw link (Gary, 16 Oct).

import type { LinkButton } from '@/data/store';

export interface QuickLink { id: string; group: string; title: string; sub: string; message: string; link: LinkButton; territoryId?: string }

interface OperatorRow { territory_id: string; name: string; page_url: string; review_url: string | null; phone: string | null }

function phoneText(e164: string | null): string {
  if (!e164) return '';
  const d = e164.replace(/[^\d]/g, '');
  return d.startsWith('27') && d.length === 11 ? `0${d.slice(2, 4)} ${d.slice(4, 7)} ${d.slice(7)}` : `+${d}`;
}

export function operatorPageMessage(o: { name: string; page_url: string; phone: string | null }): string {
  const phone = phoneText(o.phone);
  return `${o.name} looks after your area. Their page has their prices, reviews and online booking.`
    + (phone ? `\nYou can also call or WhatsApp them on ${phone}.` : '');
}

export function reviewMessage(o: { name: string }): string {
  return `Thank you for choosing ${o.name}! If you have a minute, a quick Google review helps us a lot.`;
}

/** Head office's message with {link} taken out: the page is the button. */
export function withoutLink(message: string): string {
  const m = message.replace(/\s*\{link\}\s*/g, ' ').trim();
  return /[:\-]$/.test(m) ? `${m.slice(0, -1).trim()}.` : m;
}

/** A page's button words: its name when short enough, else a plain "Open the page". */
export function buttonWords(label: string): string {
  const l = label.trim();
  return l.length <= 20 ? l : 'Open the page';
}

export async function loadQuickLinks(brand: 'cmb' | 'pos'): Promise<QuickLink[]> {
  const [{ data: ours }, { data: ops }] = await Promise.all([
    supabase.from('chat_quick_links').select('id, label, url, message').eq('active', true).eq('brand', brand).order('sort'),
    brand === 'cmb' ? supabase.rpc('chat_quick_link_operators') : Promise.resolve({ data: [] }),
  ]);
  const operators = (ops ?? []) as OperatorRow[];
  return [
    ...operators.map((o) => ({
      id: `page-${o.territory_id}`, group: 'Operator pages', title: o.name, sub: o.page_url.replace('https://', ''),
      message: operatorPageMessage(o), link: { label: 'Visit their page', url: o.page_url }, territoryId: o.territory_id,
    })),
    ...operators.filter((o) => o.review_url).map((o) => ({
      id: `review-${o.territory_id}`, group: 'Google reviews', title: o.name, sub: 'Ask for a Google review',
      message: reviewMessage(o), link: { label: 'Leave a review', url: o.review_url! }, territoryId: o.territory_id,
    })),
    ...((ours ?? []) as { id: string; label: string; url: string; message: string }[]).map((l) => ({
      id: l.id, group: 'Our pages', title: l.label, sub: l.url.replace('https://', ''),
      message: withoutLink(l.message), link: { label: buttonWords(l.label), url: l.url },
    })),
  ];
}
